<!-- components/landing-preview/landing-preview.component.html -->
<div class="min-h-screen bg-gray-50">

  <!-- Hero Section -->
  <section class="relative h-96 bg-gradient-to-r from-slate-900 to-slate-400 overflow-hidden">

    <!-- Imagen de fondo del hero -->
    @if (hasHeroImage) {
      <img
        [src]="data.hero.imageUrl"
        [alt]="data.hero.imageAlt"
        class="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-80">
    } @else {
      <!-- Placeholder cuando no hay imagen -->
      <div class="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-500/20 to-purple-500/20">
        <div class="text-center text-white/50">
          <svg class="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p class="text-sm">Agrega una imagen para verla aquí</p>
        </div>
      </div>
    }

    <!-- Contenido del hero -->
    <div class="relative h-full flex flex-col justify-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl">
        <h1 class="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg leading-tight">
          {{ data.hero.title }}
        </h1>

        @if (data.hero.subtitle) {
          <p class="text-xl sm:text-2xl text-white/95 drop-shadow-md leading-relaxed">
            {{ data.hero.subtitle }}
          </p>
        }
      </div>
    </div>

    <!-- Decoración del hero -->
    <div class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent"></div>
  </section>

  <!-- Contenido Principal -->
  <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

    <!-- Descripción Principal -->
    @if (hasDescription) {
      <div class="prose prose-lg max-w-none mb-12">
        <div class="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
          <p class="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
            {{ data.content.description }}
          </p>
        </div>
      </div>
    } @else {
      <div class="bg-white rounded-xl shadow-sm p-8 border border-gray-100 mb-12">
        <div class="text-center text-gray-400">
          <svg class="w-16 h-16 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p class="text-sm">Agrega una descripción principal</p>
        </div>
      </div>
    }

    <!-- HTML Personalizado -->
    @if (hasCustomHtml) {
  <div class="mb-12 custom-html-container" [innerHTML]="sanitizedCustomHtml"></div>
}

    <!-- Galería de Imágenes -->
    @if (hasGallery) {
      <div class="mb-12">
        <h2 class="text-3xl font-bold text-gray-900 mb-6">Galería</h2>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          @for (image of data.images; track image.url) {
            <div class="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              @if (image.url) {
                <img
                  [src]="image.url"
                  [alt]="image.alt_text"
                  class="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300">

                <!-- Overlay con alt text -->
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p class="text-white text-sm font-medium p-4">
                    {{ image.alt_text }}
                  </p>
                </div>
              } @else {
                <div class="w-full h-64 bg-gray-100 flex items-center justify-center">
                  <div class="text-center text-gray-400">
                    <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p class="text-xs">Vista previa pendiente</p>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </div>
    }

    <!-- Información SEO (Solo para referencia del editor) -->
    @if (data.seo.meta_description || data.seo.keywords.length > 0) {
      <div class="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-12">
        <div class="flex items-start gap-3">
          <svg class="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div class="flex-1">
            <h3 class="text-sm font-semibold text-yellow-800 mb-2">Información SEO</h3>

            @if (data.seo.meta_description) {
              <div class="mb-3">
                <p class="text-xs font-medium text-yellow-700 mb-1">Meta Description:</p>
                <p class="text-sm text-yellow-900 bg-yellow-100/50 p-2 rounded">
                  {{ data.seo.meta_description }}
                </p>
              </div>
            }

            @if (data.seo.keywords.length > 0) {
              <div>
                <p class="text-xs font-medium text-yellow-700 mb-1">Keywords:</p>
                <div class="flex flex-wrap gap-2">
                  @for (keyword of data.seo.keywords; track keyword) {
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {{ keyword }}
                    </span>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }

  </main>

  <!-- Botón CTA Flotante (Preview) -->
  <!-- <div class="fixed bottom-0 left-0 right-0 lg:bottom-8 lg:right-8 lg:left-auto lg:w-auto z-50">
    <button
      class="w-full lg:w-auto bg-gradient-to-r from-slate-600 to-slate-500 text-white font-bold
             py-4 lg:py-3 px-8 lg:rounded-full shadow-2xl
             hover:from-blue-700 hover:to-purple-700
             transform hover:scale-105 transition-all duration-300
             flex items-center justify-center gap-2"
      disabled>
      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
      </svg>
      <span>Reservar Ahora</span>
      <span class="text-xs opacity-75">(Preview)</span>
    </button>
  </div> -->

</div>
